/** Small math helpers shared across systems. Keep this dependency-free. */

import type { Vec2 } from './types';

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Frame-rate independent exponential smoothing. `rate` is roughly 1/seconds. */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

/** Map v from [inMin, inMax] to [outMin, outMax], clamped. */
export function remap(v: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  if (inMax === inMin) return outMin;
  return outMin + clamp01((v - inMin) / (inMax - inMin)) * (outMax - outMin);
}

/** Smoothstep between edge0 and edge1. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0 || 1));
  return t * t * (3 - 2 * t);
}

export function dist2(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}

export function dist(a: Vec2, b: Vec2): number {
  return Math.sqrt(dist2(a, b));
}

export function len(v: Vec2): number {
  return Math.hypot(v.x, v.z);
}

/** Normalise in place. Zero-length vectors are left alone. */
export function normalize(v: Vec2): Vec2 {
  const l = Math.hypot(v.x, v.z);
  if (l > 1e-6) {
    v.x /= l;
    v.z /= l;
  }
  return v;
}

export function vec2(x = 0, z = 0): Vec2 {
  return { x, z };
}

export function copy(dst: Vec2, src: Vec2): Vec2 {
  dst.x = src.x;
  dst.z = src.z;
  return dst;
}

/** Shortest signed angular difference from a to b, in (-PI, PI]. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

/** Rotate `from` toward `to` by at most `maxStep` radians. */
export function turnToward(from: number, to: number, maxStep: number): number {
  const d = angleDelta(from, to);
  return from + clamp(d, -maxStep, maxStep);
}

/** Heading (yaw) that points from `a` to `b`, matching the yaw=0 looks down -Z convention. */
export function headingTo(a: Vec2, b: Vec2): number {
  return Math.atan2(b.x - a.x, -(b.z - a.z));
}

/** Unit forward vector for a yaw, matching the yaw=0 looks down -Z convention. */
export function forwardFromYaw(yaw: number): Vec2 {
  return { x: Math.sin(yaw), z: -Math.cos(yaw) };
}

/**
 * The game's yaw and three.js's `rotation.y` turn opposite ways.
 *
 * `forwardFromYaw` is the convention every gameplay system uses: forward is
 * `(sin yaw, -cos yaw)`, right is `(cos yaw, sin yaw)`. A three.js object with
 * `rotation.y = yaw` faces `(-sin yaw, -cos yaw)` instead — the mirror image.
 * They agree only at yaw = 0 and yaw = PI, which is exactly why this survived a
 * smoke test: at spawn everything lines up, and it is a quarter turn later that
 * the camera points at the reflection of where the player is walking.
 *
 * Every place that hands a gameplay yaw to a three.js object goes through here.
 */
export function renderYaw(yaw: number): number {
  return -yaw;
}

/** Value noise in 1D, deterministic for a given seed. Good for flicker and drift. */
export function noise1(x: number, seed = 0): number {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f);
  return lerp(hash1(i, seed), hash1(i + 1, seed), u);
}

/** Deterministic hash to [0, 1). */
export function hash1(n: number, seed = 0): number {
  let h = Math.imul(n ^ seed, 0x27d4eb2d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/** Deterministic 2D hash to [0, 1). */
export function hash2(x: number, y: number, seed = 0): number {
  return hash1(Math.imul(x, 73856093) ^ Math.imul(y, 19349663), seed);
}
